import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface ExerciseHistoryEntry {
  date: string;
  load: number;
  label: string;
}

async function fetchExerciseHistory(exerciseId: string, studentId: string): Promise<ExerciseHistoryEntry[]> {
  // All plan_exercises that use this exercise
  const { data: planExercises } = await supabase
    .from('plan_exercises')
    .select('id')
    .eq('exercise_id', exerciseId);

  if (!planExercises?.length) return [];
  const peIds = planExercises.map(pe => pe.id);

  // Session logs for those plan_exercises, joined with session date
  const { data: logs } = await supabase
    .from('session_logs')
    .select('load_used, workout_sessions!inner(student_id, completed_at)')
    .in('plan_exercise_id', peIds)
    .not('load_used', 'is', null)
    .order('workout_sessions(completed_at)', { ascending: true });

  if (!logs?.length) return [];

  return (logs as any[])
    .filter(l => l.workout_sessions?.student_id === studentId && l.load_used != null)
    .map(l => {
      const d = new Date(l.workout_sessions.completed_at);
      return {
        date: l.workout_sessions.completed_at,
        load: l.load_used,
        label: `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`,
      };
    });
}

export function useExerciseHistory(exerciseId: string, studentId: string) {
  const { data = [], isLoading: loading } = useQuery({
    queryKey: ['exercise-history', exerciseId, studentId],
    queryFn: () => fetchExerciseHistory(exerciseId, studentId),
    enabled: !!exerciseId && !!studentId,
  });

  return { history: data, loading };
}
